import { router } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { View } from 'react-native';

import { CollapseSection } from '@/components/enrollments/collapse-section';
import { AccountManagement, StripeConnectProvider } from '@/components/payments/stripe-connect';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ErrorState } from '@/components/ui/error-state';
import { LoadingButton } from '@/components/ui/loading-button';
import { PageLayout } from '@/components/ui/page-layout';
import { SkeletonCard } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  ACTIVATION_STATE_ACTIVE,
  getSettingsMethodFee,
  SETTINGS_ACCOUNT,
  SETTINGS_COLLECTION,
  SETTINGS_DEACTIVATE,
  SETTINGS_HELP_LINK,
  SETTINGS_METHOD_FEE_UNAVAILABLE_NOTE,
  SETTINGS_METHOD_UNAVAILABLE_NOTE,
  SETTINGS_METHODS,
} from '@/content/payments';
import { useCanManagePaymentAccount } from '@/hooks/use-membership';
import {
  usePaymentAccount,
  useUpdateAcceptedMethods,
  useUpdatePaymentCollection,
} from '@/hooks/use-payment-account';
import { usePaymentPricing } from '@/hooks/use-payment-pricing';
import { showToast } from '@/store/toastStore';
import {
  PAYMENT_ACCOUNT_STATUS,
  PAYMENT_COLLECTION_MODE,
  type PaymentAccount,
  type PaymentAccountAcceptedMethods,
  type PaymentPricing,
} from '@/types/payment-account';
import { getApiErrorMessage } from '@/utils/api-error';

function goBack() {
  if (router.canGoBack()) {
    router.back();
    return;
  }
  router.replace('/(tabs)/(provider)/payments' as never);
}

function goToAbout() {
  router.push('/(tabs)/(provider)/payments/about' as never);
}

function StepCard({ children }: { children: React.ReactNode }) {
  return (
    <View
      className="gap-4 rounded-2xl border border-neutral-900/5 bg-card p-4"
      style={{ borderCurve: 'continuous' }}
    >
      {children}
    </View>
  );
}

/**
 * Linha de uma forma aceita: mesma tipografia dos outros títulos de campo
 * (`SwitchRow`, `wizard/field.tsx`) — a nota vira "ainda não disponível"
 * quando a subconta não habilita a forma (`pixEnabled`/`cardEnabled`/
 * `boletoEnabled`, T1.2), em vez da taxa.
 */
function MethodSwitchRow({
  label,
  note,
  checked,
  disabled,
  onChange,
  testID,
}: {
  label: string;
  note: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
  testID?: string;
}) {
  return (
    <View className="flex-row items-center gap-3 py-1.5">
      <View className="flex-1 gap-0.5">
        <ThemedText className="font-semibold text-[15px] leading-5 text-neutral-900">
          {label}
        </ThemedText>
        <ThemedText className="text-xs leading-4 text-neutral-500">{note}</ThemedText>
      </View>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} testID={testID} />
    </View>
  );
}

type MethodKey = keyof PaymentAccountAcceptedMethods;

const METHOD_ORDER: { key: MethodKey; label: string; enabledField: keyof PaymentAccount }[] = [
  { key: 'pix', label: SETTINGS_METHODS.pixLabel, enabledField: 'pixEnabled' },
  { key: 'card', label: SETTINGS_METHODS.cardLabel, enabledField: 'cardEnabled' },
  { key: 'boleto', label: SETTINGS_METHODS.boletoLabel, enabledField: 'boletoEnabled' },
];

/**
 * Formas aceitas (plano 6.5, item 1): `PATCH /payment-account/accepted-methods`
 * troca as três juntas — cada toque manda o objeto inteiro com a forma
 * trocada. Estado local otimista (mesmo padrão de `toggleAlerts` em
 * `team/settings.tsx`): liga na hora, desfaz se o servidor recusar — inclui
 * o 400 `ACCEPTED_METHODS_EMPTY` ("desligar a última forma"), cuja frase
 * real já passa por `getApiErrorMessage`.
 */
function AcceptedMethodsSection({
  account,
  pricing,
  pricingLoading,
  canManage,
}: {
  account: PaymentAccount;
  /** `undefined` com `pricingLoading: false` cobre tanto "erro" quanto "sem
   *  dados ainda" — as linhas aparecem sem a taxa nos dois casos, nunca
   *  giram para sempre (T7.1 passou a exigir `canManage` no pricing: quem
   *  não gerencia recebe 403 aqui, um caso normal, não uma falha a repetir). */
  pricing: PaymentPricing | undefined;
  pricingLoading: boolean;
  canManage: boolean;
}) {
  const updateMethods = useUpdateAcceptedMethods();
  const [methods, setMethods] = useState<PaymentAccountAcceptedMethods>(
    account.acceptedMethods ?? { pix: true, card: true, boleto: true },
  );

  useEffect(() => {
    if (account.acceptedMethods) setMethods(account.acceptedMethods);
  }, [account.acceptedMethods]);

  const toggle = async (key: MethodKey, checked: boolean) => {
    const previous = methods;
    const next = { ...methods, [key]: checked };
    setMethods(next);
    try {
      await updateMethods.mutateAsync(next);
    } catch (err) {
      setMethods(previous);
      showToast(getApiErrorMessage(err, 'Não foi possível salvar agora.'), 'error');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{SETTINGS_METHODS.title}</CardTitle>
      </CardHeader>
      <CardContent className="gap-1">
        {pricingLoading ? (
          <SkeletonCard lines={3} />
        ) : (
          METHOD_ORDER.map(({ key, label, enabledField }) => {
            const enabledByAccount = Boolean(account[enabledField]);
            const note = !enabledByAccount
              ? SETTINGS_METHOD_UNAVAILABLE_NOTE
              : pricing
                ? getSettingsMethodFee(key, pricing)
                : SETTINGS_METHOD_FEE_UNAVAILABLE_NOTE;
            return (
              <MethodSwitchRow
                key={key}
                label={label}
                note={note}
                checked={methods[key]}
                disabled={!canManage || !enabledByAccount || updateMethods.isPending}
                onChange={(checked) => void toggle(key, checked)}
                testID={`payment-settings-method-${key}`}
              />
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Conta bancária e cadastro (plano 6.5, item 2): o componente **Gestão da
 * conta** do Stripe (T4.4), a única forma de mudar banco/dados depois da
 * ativação no caminho B. Risco herdado da T4.4/T0.3: os componentes do
 * Connect não são garantidos dentro de WebView — ainda sem teste em
 * aparelho. Se falhar: plano B é uma página web nossa via
 * `expo-web-browser` (navegador in-app, não WebView) com login por token de
 * uso único, no lugar deste bloco.
 */
function AccountManagementSection() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{SETTINGS_ACCOUNT.title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ThemedText className="pb-3 text-sm leading-5 text-neutral-500">
          {SETTINGS_ACCOUNT.helper}
        </ThemedText>
        <StripeConnectProvider>
          <AccountManagement />
        </StripeConnectProvider>
      </CardContent>
    </Card>
  );
}

/**
 * Liga/desliga (plano 6.5, item 3): confirmação dentro da própria tela, sem
 * modal genérico — reaproveita `CollapseSection` (`collapse-reveal`): o
 * cabeçalho é o próprio toque de "Desligar", o corpo (explicação + Cancelar/
 * Desligar) abre por baixo. Ligar não pede confirmação (mesmo padrão de
 * `ActiveScreen`, `payment-account-status-screen.tsx`): a diferença entre os
 * dois botões é só `convertOpen`, nunca risco de religar à revelia.
 */
function CollectionSection({
  account,
  canManage,
}: {
  account: PaymentAccount;
  canManage: boolean;
}) {
  const updateCollection = useUpdatePaymentCollection();
  const [confirmingOff, setConfirmingOff] = useState(false);
  const isAutomatic = account.paymentCollection === PAYMENT_COLLECTION_MODE.AUTOMATIC;

  const turnOff = async () => {
    try {
      await updateCollection.mutateAsync({ mode: PAYMENT_COLLECTION_MODE.MANUAL });
      setConfirmingOff(false);
      showToast('Cobrança automática desligada.', 'success');
    } catch (err) {
      showToast(getApiErrorMessage(err, 'Não foi possível desligar agora.'), 'error');
    }
  };

  const turnOn = async (convertOpen: boolean) => {
    try {
      await updateCollection.mutateAsync({ mode: PAYMENT_COLLECTION_MODE.AUTOMATIC, convertOpen });
      showToast(
        convertOpen
          ? 'Pronto! Cobranças em aberto passando para automático.'
          : 'Pronto! As próximas cobranças já são automáticas.',
        'success',
      );
    } catch (err) {
      showToast(getApiErrorMessage(err, 'Não foi possível ligar agora. Tente de novo.'), 'error');
    }
  };

  if (!canManage) {
    return (
      <StepCard>
        <ThemedText className="font-semibold text-[15px] leading-5 text-neutral-900">
          {SETTINGS_COLLECTION.title}
        </ThemedText>
        <ThemedText className="text-sm leading-5 text-neutral-500">
          {isAutomatic ? SETTINGS_COLLECTION.onStatus : SETTINGS_COLLECTION.offStatus} ·{' '}
          {SETTINGS_COLLECTION.readOnlyNote}
        </ThemedText>
      </StepCard>
    );
  }

  if (isAutomatic) {
    return (
      <CollapseSection
        title={SETTINGS_DEACTIVATE.title}
        status={SETTINGS_COLLECTION.onStatus}
        expanded={confirmingOff}
        onToggle={() => setConfirmingOff((value) => !value)}
      >
        <View className="gap-3">
          <ThemedText className="text-sm leading-5 text-neutral-600">
            {SETTINGS_DEACTIVATE.body}
          </ThemedText>
          <View className="flex-row gap-3">
            <Button
              variant="outline"
              className="flex-1"
              title={SETTINGS_DEACTIVATE.cancelButton}
              onPress={() => setConfirmingOff(false)}
              disabled={updateCollection.isPending}
            />
            <LoadingButton
              variant="destructive"
              className="flex-1"
              title={SETTINGS_DEACTIVATE.confirmButton}
              loading={updateCollection.isPending}
              onPress={() => void turnOff()}
              testID="payment-settings-deactivate-confirm"
            />
          </View>
        </View>
      </CollapseSection>
    );
  }

  if (!account.chargesEnabled) {
    return (
      <StepCard>
        <ThemedText className="font-semibold text-[15px] leading-5 text-neutral-900">
          {SETTINGS_COLLECTION.title}
        </ThemedText>
        <ThemedText className="text-sm leading-5 text-neutral-500">
          {SETTINGS_COLLECTION.offStatus} · {SETTINGS_COLLECTION.pendingNote}
        </ThemedText>
      </StepCard>
    );
  }

  return (
    <StepCard>
      <ThemedText className="font-semibold text-[15px] leading-5 text-neutral-900">
        {SETTINGS_COLLECTION.title}
      </ThemedText>
      <ThemedText className="text-sm leading-5 text-neutral-500">
        {ACTIVATION_STATE_ACTIVE.convertOpenPrompt}
      </ThemedText>
      <LoadingButton
        title={ACTIVATION_STATE_ACTIVE.convertOpenButton}
        loading={updateCollection.isPending}
        disabled={updateCollection.isPending}
        onPress={() => void turnOn(true)}
        testID="payment-settings-turn-on-convert-open"
      />
      <Button
        variant="outline"
        title={ACTIVATION_STATE_ACTIVE.skipButton}
        disabled={updateCollection.isPending}
        onPress={() => void turnOn(false)}
        testID="payment-settings-turn-on-skip"
      />
    </StepCard>
  );
}

/**
 * Configurar recebimento (T6.4, plano 6.5): formas aceitas com taxa, conta
 * bancária (Gestão da conta do Stripe) e liga/desliga automático. Rota nova,
 * alcançável pelo ícone de configurações em `payments/index.tsx`. Ações
 * (trocar forma, mexer na conta, ligar/desligar) só para quem gerencia
 * (`useCanManagePaymentAccount`, mesma regra do `PaymentAccountStatusScreen`
 * e do servidor); quem só acompanha vê formas e status, sem editar.
 */
export default function PaymentSettingsScreen() {
  const canManage = useCanManagePaymentAccount();
  const { data: statusData, isLoading, isFetched, isError, error, refetch } = usePaymentAccount();
  const pricing = usePaymentPricing();
  const account = statusData?.account ?? null;

  // Só decide "precisa ativar" depois de saber que a busca deu certo — um
  // erro de rede nunca pode ser lido como "sem conta" e mandar para o
  // assistente de ativação por engano.
  const needsActivation =
    isFetched &&
    !isError &&
    (!account ||
      account.status === PAYMENT_ACCOUNT_STATUS.DRAFT ||
      account.status === PAYMENT_ACCOUNT_STATUS.VERIFYING);

  useEffect(() => {
    if (needsActivation) {
      router.replace('/(tabs)/(provider)/payments/activate' as never);
    }
  }, [needsActivation]);

  if (isLoading || !isFetched) {
    return (
      <PageLayout title="Configurar recebimento" onBack={goBack}>
        <SkeletonCard showHeader lines={3} />
        <SkeletonCard showHeader lines={2} />
      </PageLayout>
    );
  }

  if (isError) {
    return (
      <PageLayout title="Configurar recebimento" onBack={goBack}>
        <ErrorState
          title="Não foi possível carregar"
          message={getApiErrorMessage(error, 'Tente de novo em instantes.')}
          onRetry={() => void refetch()}
        />
      </PageLayout>
    );
  }

  if (needsActivation || !account) {
    return (
      <PageLayout title="Configurar recebimento" onBack={goBack}>
        <SkeletonCard showHeader lines={3} />
        <SkeletonCard showHeader lines={2} />
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Configurar recebimento" onBack={goBack}>
      <AcceptedMethodsSection
        account={account}
        pricing={pricing.data}
        pricingLoading={pricing.isLoading}
        canManage={canManage}
      />

      {canManage ? <AccountManagementSection /> : null}

      <CollectionSection account={account} canManage={canManage} />

      <Button
        variant="outline"
        title={SETTINGS_HELP_LINK}
        onPress={goToAbout}
        testID="payment-settings-about-link"
      />
    </PageLayout>
  );
}
